import { ActionsRenderCell } from '@/components/bulkQueue/cells/ActionsRender';
import { FulfillmentWindowRenderCell } from '@/components/bulkQueue/cells/FulfillmentWindowRender';
import { OrderRenderCell } from '@/components/bulkQueue/cells/OrderRender';
import { PatientRenderCell } from '@/components/bulkQueue/cells/PatientRender';
import { PriorityRenderCell } from '@/components/bulkQueue/cells/PriorityRender';
import { ClinicianRenderCell } from '@/components/bulkQueue/cells/clinician/ClinicianRender';
import { DateRenderCell } from '@/components/bulkQueue/cells/date/DateRender';
import {
    ContextServicesSelectionCell,
    ServicesSelectionHeader,
} from '@/components/bulkQueue/cells/services/ServicesSelection';
import { getSetCellProps } from '@/components/bulkQueue/tables/components';
import { VisitTableRow } from '@/components/bulkQueue/tables/queries';
import { BulkServicesAssignmentTableQuery } from '@/components/bulkQueue/tables/services/BulkServicesAssignmentTable.gql.types';
import {
    BulkServicesContextProvider,
    useBulkServicesContext,
} from '@/components/bulkQueue/tables/services/BulkServicesContext';
import { useConnectedBulkTable } from '@/components/bulkQueue/tables/useConnectedBulkTable';
import { Pagination } from '@/components/bulkQueue/tables/useUnifiedPagination';
import { ServicesTableRow, ServicesVisitRow } from '@/components/bulkQueue/types';
import { rowToLink } from '@/components/bulkQueue/utils';
import { MapFriendlyVisit } from '@/components/mapping/VisitsMap/VisitsMap.types';
import { useSetMapFriendlyVisits } from '@/components/mapping/VisitsMap/useSetMapFriendlyVisits';
import { AxleTable, RawColumn, ServerSideSort } from '@/components/tables/AxleTable';
import { VisitColumns } from '@/components/visits/tables/columns';
import { visitOrVisitRequestTableRowFragmentToMapFriendlyVisits } from '@/components/visits/utils/visitOrVisitRequestTableRowFragmentToMapFriendlyVisits';
import { PAGE_BODY_SKELETON_HEIGHT, SimpleSkeleton } from '@/fetching/suspense';
import { gql } from '@apollo/client';
import { Typography } from '@mui/material';
import { FC, useCallback, useMemo } from 'react';

const servicesAssignmentVisit = gql`
    fragment ServicesAssignmentVisit on Visit {
        availableServices {
            id
            name
            isVirtual
        }
    }
`;

export const BULK_SERVICES_ASSIGNMENT_TABLE_QUERY = gql`
    query BulkServicesAssignmentTable(
        $visitFilters: VisitFilter
        $visitPagination: PaginationInput
        $visitOrdering: VisitOrdering
    ) {
        visits(filters: $visitFilters, pagination: $visitPagination, ordering: $visitOrdering) {
            totalCount
            items {
                ...VisitTableRow
                ...ServicesAssignmentVisit
            }
        }
    }
    ${VisitTableRow}
    ${servicesAssignmentVisit}
`;

type BulkServicesAssignmentTableProps = {
    rows: ServicesTableRow[];
    pagination: Pagination;
    title?: string;
    serverSideSort: ServerSideSort;
};

export const BulkServicesAssignmentTable: FC<BulkServicesAssignmentTableProps> = (props) => {
    const { rows, pagination, title, serverSideSort } = props;
    const selection = useBulkServicesContext();

    const columns: RawColumn<ServicesTableRow>[] = useMemo(
        () => [
            {
                field: 'sortPriority',
                label: 'Priority',
                rowToComponent: (entity) => <PriorityRenderCell entity={entity} />,
                options: { ...VisitColumns.priorityColumnOptions, sort: false },
            },
            {
                field: 'patient.fullName',
                label: 'Patient',
                rowToComponent: (entity) => <PatientRenderCell entity={entity} />,
            },
            {
                field: 'sortServicesName',
                label: 'Services',
                rowToComponent: (entity) => <ContextServicesSelectionCell entity={entity} />,
                options: {
                    sort: false,
                    setCellProps: (_value, rowIndex) =>
                        getSetCellProps(
                            selection.selectedCount === 0 ||
                                selection.getSelectionState(rows[rowIndex]).isSelected,
                        ),
                    customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
                        <ServicesSelectionHeader
                            key={columnMeta.name + '-header'}
                            columnMeta={columnMeta}
                            handleToggleColumn={handleToggleColumn}
                            sortOrder={sortOrder}
                        />
                    ),
                },
            },
            {
                field: 'cliniciansSortKey',
                label: 'Clinician',
                rowToComponent: (entity) => <ClinicianRenderCell entity={entity} />,
                options: { sort: false },
            },
            {
                field: 'sortDate',
                label: 'Date',
                rowToComponent: (entity) => <DateRenderCell entity={entity} />,
            },
            {
                field: 'fulfillmentWindow.startDate',
                label: 'Fulfillment Window',
                rowToComponent: (entity) => <FulfillmentWindowRenderCell entity={entity} />,
            },
            {
                field: 'orderSortKey',
                label: 'Order',
                rowToComponent: (entity) => <OrderRenderCell entity={entity} />,
                options: { sort: false },
            },
            {
                field: '',
                label: 'Actions',
                rowToComponent: (entity) => <ActionsRenderCell entity={entity} />,
            },
        ],
        [rows, selection],
    );

    return (
        <AxleTable
            title={
                typeof title === 'string' ? (
                    <Typography variant='h2'>{title}</Typography>
                ) : undefined
            }
            toolbar={false}
            dynamicPaging={pagination}
            data={rows}
            rowToLink={rowToLink}
            columns={columns}
            serverSideSort={serverSideSort}
        />
    );
};

type ConnectedBulkServicesAssignmentTableProps = {
    title?: string;
};

export const ConnectedBulkServicesAssignmentTable: FC<ConnectedBulkServicesAssignmentTableProps> = (
    props,
) => {
    const { title } = props;
    const { rows, pagination, serverSideSort, showSkeleton } = useConnectedBulkTable<
        BulkServicesAssignmentTableQuery,
        ServicesVisitRow
    >({ query: BULK_SERVICES_ASSIGNMENT_TABLE_QUERY });

    const setMapFriendlyVisits: () => MapFriendlyVisit[] = useCallback(() => {
        return visitOrVisitRequestTableRowFragmentToMapFriendlyVisits(rows);
    }, [rows]);

    useSetMapFriendlyVisits(setMapFriendlyVisits);

    if (showSkeleton) {
        return <SimpleSkeleton height={PAGE_BODY_SKELETON_HEIGHT} />;
    }

    return (
        <BulkServicesContextProvider entities={rows}>
            <BulkServicesAssignmentTable
                rows={rows}
                title={title}
                pagination={pagination}
                serverSideSort={serverSideSort}
            />
        </BulkServicesContextProvider>
    );
};
